import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { blogPosts } from '../utils/constants';

const BlogDetails = () => {
    const { id } = useParams();
    const blog = blogPosts.find(post => post.id === parseInt(id));

    if (!blog) {
        return (
            <div className="container py-5 text-center">
                <h2>Blog post not found</h2>
                <Link to="/blog" className="btn btn-primary-custom mt-3">
                    Back to Blog
                </Link>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{blog.title} | LAMSAT Blog</title>
                <meta name="description" content={blog.excerpt} />
                <meta property="og:title" content={blog.title} />
                <meta property="og:description" content={blog.excerpt} />
                <meta property="og:image" content={blog.image} />
            </Helmet>

            {/* Blog Header */}
            <section className="py-5 bg-primary-blue">
                <div className="container">
                    <div className="text-center text-white">
                        <div className="mb-3">
                            <span className="badge bg-primary-red me-2">{blog.category}</span>
                            <span className="text-light">{blog.date}</span>
                        </div>
                        <h1 className="display-5 fw-bold mb-4">{blog.title}</h1>
                        <div className="d-flex justify-content-center align-items-center">
                            <div className="d-flex align-items-center">
                                <img
                                    src={blog.author.avatar}
                                    alt={blog.author.name}
                                    className="rounded-circle me-3"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                                <div>
                                    <p className="mb-0 fw-bold">{blog.author.name}</p>
                                    <p className="mb-0 small" style={{ color: '#ccc' }}>{blog.author.role}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Blog Content */}
            <section className="py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <motion.div
                            className="col-lg-8"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="mb-5">
                                <img
                                    src={blog.image}
                                    alt={blog.title}
                                    className="img-fluid rounded-3 w-100 mb-4"
                                    style={{ maxHeight: '500px', objectFit: 'cover' }}
                                />
                            </div>

                            <div className="blog-content">
                                <p className="lead mb-4">{blog.excerpt}</p>

                                <div className="mb-5">
                                    <h3 className="mb-3">Introduction</h3>
                                    <p>
                                        In the rapidly evolving construction industry of the UAE, staying ahead
                                        requires not just experience but also innovation and adaptability.
                                        At LAMSAT, we combine decades of expertise with cutting-edge techniques
                                        to deliver exceptional results for every project.
                                    </p>
                                </div>

                                <div className="mb-5">
                                    <h3 className="mb-3">Key Considerations</h3>
                                    <p>
                                        Quality engineering services are crucial for the longevity and safety
                                        of any structure. Our approach focuses on several key aspects:
                                    </p>
                                    <ul className="mb-4">
                                        <li className="mb-2">Comprehensive project planning and assessment</li>
                                        <li className="mb-2">Use of premium materials and equipment</li>
                                        <li className="mb-2">Strict adherence to safety protocols and regulations</li>
                                        <li className="mb-2">Regular quality checks and maintenance</li>
                                        <li className="mb-2">Sustainable and energy-efficient solutions</li>
                                    </ul>
                                </div>

                                <div className="mb-5 p-4 bg-off-white rounded-3">
                                    <h4 className="mb-3">Expert Tip:</h4>
                                    <p className="mb-0 fst-italic">
                                        "Regular maintenance of HVAC and electrical systems can extend their
                                        lifespan by up to 40% and significantly reduce energy costs. Schedule
                                        professional inspections at least twice a year for optimal performance."
                                    </p>
                                </div>

                                <div className="mb-5">
                                    <h3 className="mb-3">Conclusion</h3>
                                    <p>
                                        Whether you're planning a new construction project or maintaining
                                        existing infrastructure, partnering with experienced professionals
                                        ensures quality, safety, and peace of mind. At LAMSAT, we're committed
                                        to delivering excellence in every project we undertake.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-5 pt-4 border-top">
                                <div className="d-flex justify-content-between align-items-center">
                                    <Link to="/blog" className="btn btn-outline-custom">
                                        <i className="fas fa-arrow-left me-2"></i> Back to Blog
                                    </Link>
                                    <div className="social-share">
                                        <span className="me-3">Share:</span>
                                        <a href="#" className="text-primary-red me-3">
                                            <i className="fab fa-facebook-f"></i>
                                        </a>
                                        <a href="#" className="text-primary-red me-3">
                                            <i className="fab fa-twitter"></i>
                                        </a>
                                        <a href="#" className="text-primary-red me-3">
                                            <i className="fab fa-linkedin-in"></i>
                                        </a>
                                        <a href="#" className="text-primary-red">
                                            <i className="fab fa-whatsapp"></i>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Related Posts */}
            <section className="py-5 bg-off-white">
                <div className="container">
                    <h3 className="mb-4 text-center">Related Posts</h3>
                    <div className="row">
                        {blogPosts
                            .filter(post => post.id !== blog.id)
                            .slice(0, 3)
                            .map((relatedPost, index) => (
                                <div key={relatedPost.id} className="col-md-4 mb-4">
                                    <div className="card h-100">
                                        <img
                                            src={relatedPost.image}
                                            className="card-img-top"
                                            alt={relatedPost.title}
                                            style={{ height: '200px', objectFit: 'cover' }}
                                        />
                                        <div className="card-body">
                                            <h6 className="card-title">{relatedPost.title}</h6>
                                            <p className="card-text small text-muted">{relatedPost.excerpt.substring(0, 100)}...</p>
                                            <Link to={`/blog/${relatedPost.id}`} className="btn btn-sm btn-outline-custom">
                                                Read More
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default BlogDetails;